/**
 * The Chase queue's "By carer" view (Tracey's workbook → Chase, 2026-10-13):
 * pure, so the grouping and wording are testable without a DOM (the
 * filters.ts / age.ts split).
 *
 * Her spreadsheet was organised by support worker — who owes what, how many,
 * what happened about it. A carer group holds their OPEN cases (the queue's
 * rows) and the last month's CLOSED ones (received, not owed, resolved), keyed
 * exactly as the Carer filter keys them (`carerKeysOf`), so a carer is one
 * group whether they own a case or are only on the rota row.
 */
import { spanOf } from "./age";
import { carerKeysOf, passesDateRange, passesFilters, type DateRange, type FilterableRow, type FilterChoice } from "./filters";

/** "At or over 3" — the office's line for a final reminder and escalation
 *  (Tracey's rule). Display only; agent.chase's policy decides any sending. */
export const AT_OR_OVER = 3;

export interface ClosedRow {
  caseId: string;
  clientName: string | null;
  oneTouchClientId: number | null;
  slots: { date: string | null; session: string | null; start: number | null; finish: number | null }[];
  dueEnd: string | null;
  closedAt: string | null;
  lateMinutes: number | null;
  outcome: "received" | "resolved" | "not-owed" | "cancelled-upstream";
  evidence?: string | null;
  code?: string | null;
  display?: string | null;
  text?: string | null;
  byEmail?: string | null;
  owner: { personId: string; name: string | null } | null;
  candidates: { oneTouchCarerId: number | null; personId: string | null; name: string | null }[];
}

export interface CarerGroup<R> {
  key: string;
  personId: string | null;
  name: string;
  open: R[];
  closed: ClosedRow[];
  services: string[];
  hot: boolean;
}

/** Closed rows have no service of their own; grouping only needs who. */
function asFilterable(c: ClosedRow): FilterableRow {
  return { ...c, service: null };
}

export function groupByCarer<R extends FilterableRow & { caseId: string }>(
  open: R[],
  closed: ClosedRow[],
): CarerGroup<R>[] {
  const by = new Map<string, CarerGroup<R>>();
  const groupFor = (key: string, label: string): CarerGroup<R> => {
    let g = by.get(key);
    if (!g) {
      g = {
        key,
        personId: key.startsWith("p:") ? key.slice(2) : null,
        name: label,
        open: [],
        closed: [],
        services: [],
        hot: false,
      };
      by.set(key, g);
    }
    return g;
  };
  for (const r of open) {
    for (const c of carerKeysOf(r)) {
      const g = groupFor(c.key, c.label);
      g.open.push(r);
      if (r.service && !g.services.includes(r.service)) g.services.push(r.service);
    }
  }
  // Closed rows join a carer who still owes something; a carer with nothing
  // open is done, and doesn't need a card of history alone.
  for (const r of closed) {
    for (const c of carerKeysOf(asFilterable(r))) by.get(c.key)?.closed.push(r);
  }
  const groups = [...by.values()];
  for (const g of groups) g.hot = g.open.length >= AT_OR_OVER;
  return groups.sort(
    (a, b) =>
      Number(b.hot) - Number(a.hot) ||
      b.open.length - a.open.length ||
      a.name.localeCompare(b.name),
  );
}

/** The closed row's chip text: "Received · 2d 3h late", "Not owed · Client in
 *  hospital", "Resolved manually", "Visit cancelled in OneTouch". */
export function closedChip(c: ClosedRow): { text: string; tone: "ok" | "late" | "quiet" } {
  if (c.outcome === "received" || c.outcome === "resolved") {
    const verb = c.outcome === "received"
      ? c.evidence === "app-record" ? "In-app record received" : "MachForm received"
      : "Resolved manually";
    if (c.lateMinutes == null) return { text: verb, tone: "ok" };
    return c.lateMinutes > 0
      ? { text: `${verb} · ${spanOf(c.lateMinutes * 60_000)} late`, tone: "late" }
      : { text: `${verb} · on time`, tone: "ok" };
  }
  if (c.outcome === "not-owed") {
    const why = c.display && c.code !== "other" ? c.display : c.text ?? c.display ?? "no reason";
    return { text: `Not owed · ${why}`, tone: "quiet" };
  }
  // ADR-0035 am.10: a later slot of one carer's continuous stay owes no DSR
  // of its own — the stay's first shift carries it (closeReason, via omon-api).
  if (c.text === "folded-into-stay") {
    return { text: "Not owed · same stay as the shift before", tone: "quiet" };
  }
  return { text: "Visit cancelled in OneTouch", tone: "quiet" };
}

export interface EscalationState {
  status?: string | null;
  steps: Partial<Record<EscalationStep, { at: string | null; byEmail: string | null }>>;
}

export type EscalationStep = "raised" | "cci" | "investigation";

/** When the office last sent this carer a final reminder (Tracey,
 *  2026-10-30). A manual marker: she sends it from Outlook; Chase only
 *  records the date, for the CCI investigation notes. */
export interface FinalReminder {
  at: string | null;
  byEmail: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "30 Oct" (UK order, the viewer's local day); "" when unknown. Fixed month
 *  names, as noteStamp, so every browser prints the same label. */
export function ukDay(at: string | null | undefined): string {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** The card's tag: "Final reminder sent 30 Oct"; "" when none recorded. */
export function finalReminderTag(r: FinalReminder | null | undefined): string {
  const day = ukDay(r?.at);
  return day ? `Final reminder sent ${day}` : "";
}

export const ESCALATION_STEPS: { step: EscalationStep; label: string }[] = [
  { step: "raised", label: "Raised to manager" },
  { step: "cci", label: "CCI raised" },
  { step: "investigation", label: "Investigation complete" },
];

// CCI filter (Tracey, 2026-11-02): remove with the old CCI process.
/** Narrow `groups` to carers with the CCI step set, adding a card for a CCI
 *  carer who owes nothing in view but has a closed row that passes the same
 *  filters. Closed rows carry no service (omon-api), so it is borrowed from an
 *  open row for the same client; with none to borrow from the row is kept
 *  under any service, the passesDateRange rule: never hide what we can't place. */
export function onlyCciCarers<R extends FilterableRow & { caseId: string }>(
  groups: CarerGroup<R>[],
  allOpen: R[],
  closed: ClosedRow[],
  escalations: Record<string, EscalationState> | undefined,
  filters: FilterChoice,
  range: DateRange,
): CarerGroup<R>[] {
  const hasCci = (personId: string | null) => Boolean(personId && escalations?.[personId]?.steps?.cci);
  const serviceOfClient = new Map(allOpen.map((r) => [r.oneTouchClientId, r.service]));
  const inView = new Set(groups.map((g) => g.key));
  const added = new Map<string, CarerGroup<R>>();
  for (const c of closed) {
    const service = (c.oneTouchClientId != null && serviceOfClient.get(c.oneTouchClientId)) || null;
    const row = { ...c, service };
    if (!passesFilters(row, service ? filters : { ...filters, service: "" })) continue;
    if (!passesDateRange(c, range)) continue;
    for (const { key, label } of carerKeysOf(row)) {
      const personId = key.startsWith("p:") ? key.slice(2) : null;
      if (inView.has(key) || !hasCci(personId)) continue;
      const g: CarerGroup<R> = added.get(key) ?? { key, personId, name: label, open: [], closed: [], services: [], hot: false };
      added.set(key, g);
      g.closed.push(c);
      if (service && !g.services.includes(service)) g.services.push(service);
    }
  }
  // Nothing open sorts after everything open, as groupByCarer would have it.
  return [
    ...groups.filter((g) => hasCci(g.personId)),
    ...[...added.values()].sort((a, b) => a.name.localeCompare(b.name)),
  ];
}
